import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import log from 'loglevel'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { LinkIcon } from 'src/components/Icons/FileType'
import { ShowPageSectionContainer } from 'src/components/PageLayout/ShowPage'
import { ProgressMeterWithText } from 'src/components/Reusable/ProgressMeter/ProgressMeter'
import { EroDarkSVG } from 'src/components/Icons/Brand'
import {
  PrimaryButton,
  StandardButtonSize,
} from 'src/components/Reusable/Buttons'
import { DisplayCost } from 'src/components/Reusable/Cost/Cost.components'
import { InfoSection } from 'src/components/Reusable/IndexPage/InfoSection'
import { PERMISSIONS } from 'src/config/roleConfig'
import { HUBSPOT } from 'src/Utils/hubspot/hubspot.utils'
import { IndexPageActions } from 'src/components/PageLayout/IndexPage/IndexPageActions'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { useInAudit } from 'src/context/AuditContext'
import { useAuditBotModal } from 'src/components/ModalForms/Reusable/SelectControls/AuditScanModal.hooks'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { Message } from 'src/components/Reusable/Message/Message'
import { MessageType } from 'src/components/Reusable/Message/Message.constants'
import { MessageCTA } from 'src/components/Reusable/Message/Message.types'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  IconContainer,
  IconProgressRing,
  IconProgressRingCircle,
} from '../GRCDashboards/Compliance/ChecklistWidget/Card/Card.styles'
import {
  AuditPrecheckReadyContainer,
  AuditRunHeader,
  AuditScanInfoContainer,
  AuditbotSectionHeader,
  EmptyTab,
} from './AuditBot.styles'
import {
  AUDITBOT_SECTION_CONFIG,
  AuditBotCost,
  AuditbotCostTypes,
} from './AuditBot.constants'
import {
  AuditBotShowButtonContainer,
  AuditBotShowSectionExplanation,
  AuditBotShowSectionSummary,
  AuditBotStyledHeaderButton,
} from './show/AuditBotShowPage.styles'
import { CheckWithEroButton } from './reusable/CheckWithEroButton'
import { DateRange, useStartAuditRun } from './AuditBot.hooks'

export const AuditRunTitle = ({
  modelId,
  text,
  route,
  startTime,
  promptOverrideSourceName,
}: {
  modelId: string
  text: string
  startTime?: string
  route: string
  promptOverrideSourceName?: string
}): JSX.Element => {
  const { pageContext } = useParams()

  return (
    <AuditRunHeader>
      {startTime && <span>{startTime}</span>}
      <span>{text}</span>
      {promptOverrideSourceName && (
        <span title="Prompt override source">[{promptOverrideSourceName}]</span>
      )}
      <a
        href={`/${pageContext}/${route}/${modelId}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <LinkIcon />
      </a>
    </AuditRunHeader>
  )
}

export const EmptyControlAuditRuns = ({
  controlId,
  currentAudit,
  customText,
}: {
  controlId: string
  currentAudit?: AuditRecord
  customText?: string
}): JSX.Element => (
  <>
    <AuditScanInfoContainer>
      <InfoSection
        text="AI Examination and Test is powered by Trustero AI."
        link={HUBSPOT.AUDIT_SCAN_FAQ}
        isExternalLink
      />
    </AuditScanInfoContainer>
    <EmptyTab>
      <br />
      <EroDarkSVG />
      <p>{customText || 'This control has not been examined and tested.'}</p>
      <CheckWithEroButton
        controlId={controlId}
        currentAudit={currentAudit}
        customText="Examine and Test"
      />
    </EmptyTab>
  </>
)

export const AuditBotOverrideHeaderButton = ({
  onClick,
}: {
  onClick: () => void
}): JSX.Element => (
  <AuditBotShowButtonContainer>
    <AuditBotStyledHeaderButton
      size={StandardButtonSize.MEDIUM}
      onClick={onClick}
      text="Examine and Test Anyway"
      requiredPermissions={[PERMISSIONS.EDIT, PERMISSIONS.READ]}
    />
  </AuditBotShowButtonContainer>
)

export const AuditBotShowSummarySection = ({
  summary,
  overrideFunc,
}: {
  summary: AUDITBOT_SECTION_CONFIG
  overrideFunc: () => void
}): JSX.Element => {
  const canStartAuditRun = useCanPerformAction(CAPABILITY.CAN_START_AUDIT_SCANS)
  const showOverride = canStartAuditRun && summary.showOverride
  return (
    <ShowPageSectionContainer>
      <AuditbotSectionHeader>SUMMARY</AuditbotSectionHeader>
      <AuditBotShowSectionSummary>
        {summary.summary || summary.summaryText}
      </AuditBotShowSectionSummary>
      <AuditBotShowSectionExplanation>
        {summary.explanation}
      </AuditBotShowSectionExplanation>
      {showOverride && <AuditBotOverrideHeaderButton onClick={overrideFunc} />}
    </ShowPageSectionContainer>
  )
}
export const AuditScanProgress = ({
  progress,
  progressMax,
}: {
  progress: number
  progressMax: number
}): JSX.Element => {
  return (
    <ProgressMeterWithText
      progress={progress}
      progressMax={progressMax}
      noun="control"
      verb="Examining and Testing"
      description="These tests give each control close scrutiny, reviewing evidence and
    checking each control with a number of appropriate tests."
    />
  )
}

export const ScanAgainHeaderButton = ({
  auditId,
  range,
  controlIds,
}: {
  auditId?: string
  range?: DateRange
  controlIds: string[]
}): JSX.Element => {
  const canStartAuditRun = useCanPerformAction(CAPABILITY.CAN_START_AUDIT_SCANS)
  const startAuditRun = useStartAuditRun()
  const startFunc = async () => {
    try {
      await startAuditRun(auditId, range, controlIds)
    } catch (error) {
      log.error(`Error starting audit run for auditId ${auditId}`, error)
    }
  }
  return (
    <AuditBotStyledHeaderButton
      size={StandardButtonSize.MEDIUM}
      onClick={startFunc}
      requiredPermissions={[PERMISSIONS.EDIT, PERMISSIONS.READ]}
      text="Examine and Test These Controls Again"
      isDisabled={!canStartAuditRun}
      disabledReason="You do not have permission to examine and test controls."
    />
  )
}

export const AuditSectionReady = ({
  ready,
  total,
  icon,
  content,
}: {
  ready: number
  total: number
  icon?: JSX.Element
  content: string | JSX.Element
}): JSX.Element => {
  const percentage = ready / (total || 1)

  return (
    <AuditPrecheckReadyContainer>
      <IconContainer>
        {icon}
        <IconProgressRing>
          <IconProgressRingCircle progress={percentage} />
        </IconProgressRing>
      </IconContainer>
      <h5>{content}</h5>
    </AuditPrecheckReadyContainer>
  )
}

export const ControlTestCost = ({
  costPerControl = AuditBotCost.ControlTest,
  controlQuantity = 0,
  type,
  style,
  onClick,
}: {
  costPerControl: AuditBotCost
  controlQuantity: number
  type?: AuditbotCostTypes
  style?: React.CSSProperties
  onClick?: () => void
}): JSX.Element => {
  const msg = `Cost: ${
    controlQuantity * costPerControl || '--'
  } AI Control Check${controlQuantity !== 1 ? 's' : ''}`

  return (
    <DisplayCost
      message={msg}
      isLarge={type === AuditbotCostTypes.Label}
      style={style}
      onClick={onClick}
    />
  )
}

export const AuditBotRoadmapMessage = (): JSX.Element => {
  const { pageContext } = useParams()
  const navigate = useNavigate()
  const ctaButton: MessageCTA = useMemo(
    () => ({
      text: 'Compliance Roadmap.',
      onClick: () => navigate(`/${pageContext}/roadmap`),
    }),
    [navigate, pageContext],
  )
  return (
    <Message
      messageType={MessageType.INFO}
      messageText="Not ready to examine and test all your controls? Start your compliance journey with our "
      ctaButton={ctaButton}
    />
  )
}

export const AuditBotIndexPageActions = (): JSX.Element => {
  const canStartAuditRun = useCanPerformAction(CAPABILITY.CAN_START_AUDIT_SCANS)
  const { auditId } = useInAudit()
  const openAuditBotModal = useAuditBotModal()
  const { openModal } = useModalState()

  return (
    <IndexPageActions
      description="Plan your next moves and keep track of progress with Trustero AI."
      learnMoreUrl={HUBSPOT.AUDIT_SCAN_FAQ}
      buttonGroup={
        <FlexColumn align={FlexAlign.FLEX_END} gap={8}>
          <PrimaryButton
            size={StandardButtonSize.SMALL}
            onClick={() =>
              auditId
                ? openAuditBotModal({
                    auditId,
                  })
                : openModal(ModalFormId.RUN_AUDITBOT)
            }
            text="Examine and Test"
            isDisabled={!canStartAuditRun}
            disabledReason="You do not have permission to examine and test controls."
          />
        </FlexColumn>
      }
    />
  )
}

export const AuditBotIndexPageButtons = (): JSX.Element => {
  return <></>
}
